import { Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, FormControlLabel, Radio, RadioGroup, FormHelperText } from "@mui/material";
import { useCreateYogurtTypeMutation, useUpdateYogurtTypeMutation, YogurtTypeItem } from "../store/api/commonAPI";
import { showToast, showError } from "../constants/toasts";
import { PricingMethod } from "../constants/enums";
import Loader from "../constants/Loader";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editYogurtType?: YogurtTypeItem | null;
}

export default function AddYogurtType({
  open,
  onClose,
  setOpen,
  editYogurtType,
}: ModalProps) {
  const [name, setName] = useState<string>("");
  const [pricing, setPricing] = useState<number>(PricingMethod.PER_100G);
  const [errors, setErrors] = useState<{
    name?: string;
    pricing?: string;
  }>({});

  const isEditMode = !!editYogurtType;

  // Create and update yogurt type mutations
  const [createYogurtType, { isLoading: isCreating }] = useCreateYogurtTypeMutation();
  const [updateYogurtType, { isLoading: isUpdating }] = useUpdateYogurtTypeMutation();

  const isLoading = isCreating || isUpdating;

  // Reset form when modal opens/closes or when editYogurtType changes
  useEffect(() => {
    if (!open) {
      setName("");
      setPricing(PricingMethod.PER_100G);
      setErrors({});
    } else if (editYogurtType) {
      // Populate form with edit data
      setName(editYogurtType.name || "");
      setPricing(editYogurtType.pricing || editYogurtType.pricingMethod || PricingMethod.PER_100G);
      setErrors({});
    } else {
      // Reset for add mode
      setName("");
      setPricing(PricingMethod.PER_100G);
      setErrors({});
    }
  }, [open, editYogurtType]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setName(newName);
    // Clear name error when user types
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handlePricingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value);
    setPricing(value);
    // Clear pricing error when user changes it
    if (errors.pricing) {
      setErrors((prev) => ({ ...prev, pricing: undefined }));
    }
  };

  // Validate form fields
  const validateForm = (): boolean => {
    const newErrors: {
      name?: string;
      pricing?: string;
    } = {};

    // Validate name
    if (!name.trim()) {
      newErrors.name = "Type name is required";
    } else if (name.trim().length < 2) {
      newErrors.name = "Type name must be at least 2 characters";
    } else if (name.trim().length > 100) {
      newErrors.name = "Type name must be less than 100 characters";
    }

    // Validate pricing method
    if (!pricing || (pricing !== PricingMethod.PER_100G && pricing !== PricingMethod.SIZE_WISE)) {
      newErrors.pricing = "Please select a valid pricing method";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      let response;
      
      if (isEditMode && editYogurtType) {
        // Update existing yogurt type
        response = await updateYogurtType({
          id: editYogurtType._id || editYogurtType.id || "",
          name: name.trim(),
          pricing: pricing,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage = response?.message || `Yogurt type "${name.trim()}" has been updated successfully`;
        showToast(successMessage);
      } else {
        // Create new yogurt type
        response = await createYogurtType({
          name: name.trim(),
          pricing: pricing,
        }).unwrap();

        // Show success message from API response or default message
        const successMessage = response?.message || `Yogurt type "${name.trim()}" has been created successfully`;
        showToast(successMessage);
      }

      // Reset form and close modal
      setName("");
      setPricing(PricingMethod.PER_100G);
      setErrors({});
      setOpen(false);
      onClose(); // Call onClose to trigger refetch in parent
    } catch (error: any) {
      console.error(`Error ${isEditMode ? "updating" : "creating"} yogurt type:`, error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${isEditMode ? "update" : "create"} yogurt type. Please try again.`;

      showError(errorMessage);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setName("");
      setPricing(PricingMethod.PER_100G);
      setErrors({});
      setOpen(false);
      onClose();
    }
  };

  return (
    <>
      <Loader isLoad={isLoading} />
      <Modal
        className="modal addType_modal"
        open={open}
        onClose={handleClose}
      >
        <div className="modal-dialog">
          <div className="modal-body">
            <div className="modal_title">
              <h2>{isEditMode ? "Edit Yogurt Type" : "Add New Yogurt Type"}</h2>
              <button
                type="button"
                className="btn-close"
                onClick={handleClose}
                aria-label="Close"
                disabled={isLoading}
              >
                <CloseIcon />
              </button>
            </div>

            <form className="form" onSubmit={handleSubmit}>
              <div className="control_group">
                <label htmlFor="type-name">Type Name</label>
                <TextField
                  id="type-name"
                  placeholder="e.g., Frozen Greek Yogurt, Classic Greek Yogurt"
                  fullWidth
                  hiddenLabel
                  value={name}
                  onChange={handleNameChange}
                  error={!!errors.name}
                  disabled={isLoading}
                />
                {errors.name && (
                  <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                    {errors.name}
                  </FormHelperText>
                )}
              </div>

              <div className="control_group">
                <label htmlFor="pricing-method">Pricing Method</label>
                <RadioGroup
                  aria-labelledby="pricing-method-label"
                  value={pricing.toString()}
                  onChange={handlePricingChange}
                  name="pricing-method"
                >
                  <FormControlLabel 
                    value={PricingMethod.PER_100G.toString()} 
                    control={<Radio disabled={isLoading} />} 
                    label={
                      <>
                        <strong>Per 100g Pricing</strong>
                        <span>Price is calculated based on weight (per 100 grams)</span>
                      </>
                    } 
                  />
                  <FormControlLabel 
                    value={PricingMethod.SIZE_WISE.toString()} 
                    control={<Radio disabled={isLoading} />} 
                    label={
                      <>
                        <strong>Size-wise Pricing</strong>
                        <span>Fixed prices for Small, Medium, and Large sizes</span>
                      </>
                    } 
                  />
                </RadioGroup>
                {errors.pricing && (
                  <FormHelperText error style={{ marginTop: '4px', marginLeft: '14px' }}>
                    {errors.pricing}
                  </FormHelperText>
                )}
              </div>

              <div className="footer_btn_flex">
                <Button
                  type="button"
                  className="btn btn_grey"
                  onClick={handleClose}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="btn btn_primary"
                  disabled={isLoading}
                >
                  {isLoading ? (isEditMode ? "Updating..." : "Adding...") : (isEditMode ? "Update Type" : "Add Type")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </Modal>
    </>
  );
}
